import { useState } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Plus, Search, ChevronLeft, ChevronRight, UserPlus } from 'lucide-react';
import { api } from '@/lib/api';
import { toNationalDisplay } from '@shared/phone';
import { useCurrentAdmin } from '@/hooks/use-current-admin';
import { AdminShell } from '@/components/admin-shell';
import { TenantStatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TenantFormDialog } from '@/components/tenant-form-dialog';

interface TenantRow {
  id: string;
  fullName: string;
  displayName: string;
  whatsappNumber: string;
  email: string | null;
  status: 'onboarding' | 'active' | 'paused' | 'cancelled';
  createdAt: string;
}

interface TenantsResponse {
  items: TenantRow[];
  page: number;
  pageSize: number;
  total: number;
}

export function TenantsListPage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading: loadingMe } = useCurrentAdmin();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const pageSize = 25;

  const { data, isLoading } = useQuery({
    queryKey: ['tenants', { search, page, pageSize }],
    queryFn: () => {
      const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (search) qs.set('search', search);
      return api<TenantsResponse>(`/api/admin/tenants?${qs.toString()}`);
    },
    enabled: !!me,
    staleTime: 1000 * 30,
  });

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/login');
    return null;
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <AdminShell>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Contactos</h1>
          <p className="text-sm text-muted-foreground">
            Contactos gestionados por el equipo Actuo
          </p>
        </div>
        <Button className="w-full sm:w-auto" onClick={() => setShowCreate(true)}>
          <Plus />
          Nuevo contacto
        </Button>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Buscar por nombre, WhatsApp o email…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="pl-9"
        />
      </div>

      <div className="rounded-lg border border-border bg-background shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>WhatsApp</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Creado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  Cargando…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && data?.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-12 text-center">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <UserPlus className="size-8" />
                    <p>No hay contactos todavía.</p>
                    <Button variant="link" size="sm" onClick={() => setShowCreate(true)}>
                      Crear el primero
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {data?.items.map((t) => (
              <TableRow
                key={t.id}
                className="hover:bg-accent cursor-pointer"
                onClick={() => navigate(`/admin/tenants/${t.id}`)}
              >
                <TableCell className="font-medium text-foreground">{t.fullName}</TableCell>
                <TableCell className="text-muted-foreground font-mono text-xs">{toNationalDisplay(t.whatsappNumber)}</TableCell>
                <TableCell className="text-muted-foreground">{t.email ?? '—'}</TableCell>
                <TableCell>
                  <TenantStatusBadge status={t.status} />
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {new Date(t.createdAt).toLocaleDateString('es-PY')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {data && data.total > 0 && (
        <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, data.total)} de {data.total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Siguiente
              <ChevronRight />
            </Button>
          </div>
        </div>
      )}

      <TenantFormDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        onCreated={(id) => navigate(`/admin/tenants/${id}`)}
      />
    </AdminShell>
  );
}
